import React from 'react';

interface RootErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  RootErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<RootErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Add to debug overlay
    const addDebugLog = (message: string, type: string = 'error') => {
      try {
        const logContainer = document.getElementById('debug-log-container');
        if (!logContainer) return;
        
        const logEntry = document.createElement('div');
        const timestamp = new Date().toLocaleTimeString();
        const colors: Record<string, string> = {
          info: '#60a5fa',
          success: '#4ade80',
          warn: '#fbbf24',
          error: '#f87171',
          step: '#a78bfa'
        };
        
        logEntry.style.cssText = `
          padding: 8px;
          margin-bottom: 4px;
          border-left: 3px solid ${colors[type] || colors.error};
          background: rgba(255, 255, 255, 0.05);
          word-break: break-word;
        `;
        logEntry.innerHTML = `<span style="color: #9ca3af;">[${timestamp}]</span> <span style="color: ${colors[type] || colors.error};">${message}</span>`;
        logContainer.appendChild(logEntry);
        logContainer.scrollTop = logContainer.scrollHeight;
      } catch (e) {
        // Silently fail if debug overlay isn't available
      }
    };
    
    addDebugLog('❌ CRITICAL: Root Error Boundary caught an error!', 'error');
    addDebugLog(`Error: ${error.message}`, 'error');
    addDebugLog(`Stack: ${error.stack?.substring(0, 300)}...`, 'error');
    if (errorInfo.componentStack) {
      addDebugLog(`Component Stack: ${errorInfo.componentStack.substring(0, 300)}...`, 'error');
    }
    
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-bg-elevated">
          <div className="text-center max-w-md w-full">
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-lg flex items-center justify-center bg-red-100">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-forest-green mb-2">App Error</h2>
              <p className="text-text-muted mb-4">
                The app encountered an error and couldn't load properly.
              </p>
              {this.state.error && (
                <details className="mb-4 text-left bg-bg-surface/50 p-3 rounded text-xs">
                  <summary className="cursor-pointer font-semibold mb-2 text-red-600">
                    Error Details
                  </summary>
                  <pre className="whitespace-pre-wrap text-red-700 font-mono">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack && (
                      <div className="mt-2 text-gray-600">
                        {this.state.errorInfo.componentStack}
                      </div>
                    )}
                  </pre>
                </details>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-forest-green text-white rounded-lg hover:bg-forest-green/90 font-medium transition-colors"
              >
                Reload App
              </button>
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null });
                }}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

